import { TPartitionGroup, EPartitionCadence } from '../../filters/types-filters';
import { EIncentiveType } from '../types-shared';

export { EIncentiveType };

/**
 * GetParticipantDetails API response type
 */
export type TParticipation = {
  /** User alias */
  alias: string;
  /** Personnel identification number */
  personnelNumber: number;
  /** User's full name */
  fullName: string;
  /** Hierarchy of managers */
  managerHierarchy: TManagerHierarchy[];
  /** Details about termination if applicable */
  terminationDetails: TTerminationDetails;
  /** PPA sign status */
  ppaSignStatus: EPpaSignStatus;
  /** Available partitions for managers */
  availableManagerPartitions?: TPartitionGroup[];
  /** Core participation details */
  coreParticipations: TCoreParticipation[];
  /** Bonus participations (legacy, not in schema) */
  bonusParticipations?: TBonusParticipation[];
};

/**
 * Manager hierarchy object
 */
export type TManagerHierarchy = {
  /** Level in management hierarchy */
  level: number;
  /** Manager's personnel number */
  personnelNumber: number;
  /** Manager's alias */
  alias: string;
  /** Manager's first name */
  firstName: string;
  /** Manager's last name */
  lastName: string;
  /** Number of direct reports */
  directsCount: string;
};

/**
 * Details about termination if applicable
 */
export type TTerminationDetails = {
  /** Date and time of termination */
  terminationDateTime?: string | null;
  /** Reason for termination */
  terminationReason?: string | null;
};

/**
 * PPA sign status
 */
export enum EPpaSignStatus {
  Signed = 'Signed',
  NotSigned = 'NotSigned',
  NotRequired = 'NotRequired',
  Pending = 'Pending', // legacy
}

/**
 * Core participation details
 */
export type TCoreParticipation = {
  /** Participation identifier */
  participationId: number;
  /** Fiscal year identifier */
  fiscalYearId: number;
  /** Fiscal semester identifier */
  fiscalSemesterId?: number | null;
  /** Revenue map number */
  revMapNumber: number;
  /** Name of the plan */
  planName: string;
  /** Indicates if the plan is active */
  isPlanActive: boolean;
  /** Indicates if the plan has a single payline */
  isSinglePaylinePlan?: boolean;
  /** Indicates if revenue release is enabled */
  isRevenueReleaseEnabled: boolean;
  /** Start date and time of participation */
  participationStartDateTime: string;
  /** End date and time of participation */
  participationEndDateTime: string;
  /** Payment mix details */
  payMixDetails: TPayMixDetails;
  /** Business segment name */
  businessSegmentName: string;
  /** Cadence of the plan */
  planCadence: EPlanCadence;
  /** Cadence of payout */
  payoutCadence: EPartitionCadence;
  /** Available partitions for the participation */
  availablePartitions: TPartitionGroup[];
  /** Summary of the role */
  roleSummary: string;
  /** Country code */
  countryCode: string;
  /** Role of the user */
  userRole: EUserRole;
  /** Status of quota acknowledgement */
  quotaAcknowledgementStatus: EQuotaAcknowledgementStatus;
  /** First qualifier name */
  qualifier1Name: string;
  /** Second qualifier name */
  qualifier2Name: string;
  /** Name of the organization */
  organizationName: string;
  /** Award consideration (legacy, not in schema) */
  awardConsideration?: string;
};

/**
 * Payment mix details
 */
export type TPayMixDetails = {
  /** Type of incentive */
  incentiveType: EIncentiveType;
  /** Incentive plan type code */
  incentivePlanTypeCode?: string;
  /** Payscale area code */
  payscaleAreaCode: string;
  /** Base salary percentage */
  baseSalary: number;
  /** Target incentive percentage */
  targetIncentive: number;
  /** Results-based incentive mix percentage */
  rBIMix?: number;
  /** Contribution-based incentive mix percentage */
  cBIMix?: number;
};

export enum EUserRole {
  Participant = 'Participant',
  ParticipantManager = 'ParticipantManager',
  NonParticipantManager = 'NonParticipantManager',
}

export enum EPlanCadence {
  SemiAnnual = 'SemiAnnual',
  Annual = 'FullYear',
}

export enum EQuotaAcknowledgementStatus {
  QuotaPendingFCAApproval = 'QuotaPendingFCAApproval',
  QuotaAvailable = 'QuotaAvailable',
  QuotaAccepted = 'QuotaAccepted',
  UserViewedQuotaAvailable = 'UserViewedQuotaAvailable',
  QuotaRejected = 'QuotaRejected',
  ReminderSent = 'ReminderSent',
  Unknown = 'Unknown',
}

export type TBonusParticipation = {
  participationId: number;
  participationStartDate: string; // Date
  participationEndDate: string; // Date
  planName: string; // Program Name
  planId: string; // Program Id
};
